import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiGet, apiPost } from '../utils/api';
import { toast } from 'sonner';
import {
  Folder, FileText, Image, File as FileIcon, Search,
  ChevronRight, ArrowLeft, Loader2, Check, Cloud
} from 'lucide-react';

const ONEDRIVE_LOGO = "https://upload.wikimedia.org/wikipedia/commons/3/3c/Microsoft_Office_OneDrive_%282019%E2%80%93present%29.svg";

function getFileIcon(mimeType, isFolder) {
  if (isFolder) return <Folder className="w-5 h-5 text-yellow-500" />;
  if (!mimeType) return <FileIcon className="w-5 h-5 text-muted-foreground" />;
  if (mimeType.startsWith('image/')) return <Image className="w-5 h-5 text-green-500" />;
  if (mimeType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileText className="w-5 h-5 text-emerald-600" />;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <FileText className="w-5 h-5 text-orange-500" />;
  if (mimeType.includes('document') || mimeType.includes('word')) return <FileText className="w-5 h-5 text-blue-500" />;
  return <FileIcon className="w-5 h-5 text-muted-foreground" />;
}

function formatSize(bytes) {
  if (!bytes) return '';
  const k = 1024;
  if (bytes < k) return `${bytes} B`;
  if (bytes < k * k) return `${(bytes / k).toFixed(1)} KB`;
  return `${(bytes / (k * k)).toFixed(1)} MB`;
}

export default function OneDriveFilePicker({ open, onClose, cardId, onAttached }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState(null);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [folderStack, setFolderStack] = useState([{ id: null, name: 'OneDrive' }]);

  const fetchFiles = useCallback(async (folderId = null, searchQuery = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (folderId) params.append('folder_id', folderId);
      if (searchQuery) params.append('search', searchQuery);
      const resp = await apiGet(`/integrations/onedrive/files?${params.toString()}`);
      if (resp.ok) {
        const data = await resp.json();
        setFiles(data.files || []);
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load OneDrive files');
      }
    } catch {
      toast.error('Failed to connect to OneDrive');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      const current = folderStack[folderStack.length - 1];
      fetchFiles(current.id, search);
    }
  }, [open, folderStack, fetchFiles]);

  const handleSearch = (value) => {
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      const current = folderStack[folderStack.length - 1];
      fetchFiles(current.id, value);
    }, 500));
  };

  const openFolder = (file) => {
    setSearch('');
    setFolderStack([...folderStack, { id: file.id, name: file.name }]);
  };

  const goBack = () => {
    if (folderStack.length > 1) {
      setSearch('');
      setFolderStack(folderStack.slice(0, -1));
    }
  };

  const attachFile = async (file) => {
    setAttaching(file.id);
    try {
      const resp = await apiPost('/integrations/onedrive/attach', {
        card_id: cardId, file_id: file.id,
        file_name: file.name, file_mime: file.mimeType
      });
      if (resp.ok) {
        const attachment = await resp.json();
        toast.success(`${file.name} attached from OneDrive`);
        onAttached?.(attachment);
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to attach file');
      }
    } catch {
      toast.error('Failed to attach file');
    }
    setAttaching(null);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src={ONEDRIVE_LOGO} alt="OneDrive" className="w-6 h-6" />
            OneDrive Files
          </DialogTitle>
          <DialogDescription>
            Browse and attach files from your OneDrive
          </DialogDescription>
        </DialogHeader>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground overflow-x-auto">
          {folderStack.map((f, i) => (
            <span key={i} className="flex items-center gap-1 shrink-0">
              {i > 0 && <ChevronRight className="w-3 h-3" />}
              <span className={i === folderStack.length - 1 ? 'font-medium text-foreground' : ''}>{f.name}</span>
            </span>
          ))}
        </div>

        {/* Search + Back */}
        <div className="flex items-center gap-2">
          {folderStack.length > 1 && (
            <Button variant="ghost" size="icon" onClick={goBack} className="shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search files..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[400px] border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Cloud className="w-8 h-8 mb-2" />
              <p className="text-sm">No files found</p>
            </div>
          ) : (
            <div className="divide-y">
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors ${file.isFolder ? 'cursor-pointer' : ''}`}
                  onClick={() => file.isFolder && openFolder(file)}
                >
                  {getFileIcon(file.mimeType, file.isFolder)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {file.isFolder ? 'Folder' : formatSize(file.size)}
                    </p>
                  </div>
                  {!file.isFolder && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); attachFile(file); }}
                      disabled={attaching === file.id}
                      className="shrink-0"
                    >
                      {attaching === file.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3" />
                      )}
                      <span className="ml-1 text-xs">Attach</span>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
